// results: -> show all the charts
// shw the charts at the top
// if no integrationEmail -> ask if they want the full report
// if yes integrationEmail but not completed, say it is processing and they will receive an email when done
import { Box, Grid, Link, Typography, capitalize } from "@mui/material";
import logo from "assets/logos/white-logo-2.png";
import { AlertContext } from "contexts/Alert";
import { FairPaySurveyResponseContext } from "contexts/FairPaySurveyResponse";
import { useContext, useEffect, useState } from "react";

import { fetcher } from "utils/api";
import { trackEvent } from "utils/tracking";

import BiggestBrandDeal from "./BiggestBrandDeal";
import BrandBreakdown from "./BrandBreakdown";
import CityPercentile from "./CityPercentile";
import FairPayBadge from "./FairPayBadge";
import FairPayShareBadge from "./FairPayShareBadge";
import FollowersPercentile from "./FollowersPercentile";
import LocationPercentile from "./LocationPercentile";
import NichePercentile from "./NichePercentile";

const styles = {
  innerContainer: {
    maxWidth: "500px !important",
    p: 2,
  },
  logo: { width: 100, borderRadius: 2, my: 2, mx: "auto" },
  heading: {
    fontSize: "42px",
    textAlign: "center",
    maxWidth: "90%",
    fontWeight: 600,
  },
  paragraph: {
    textAlign: "center",
    opacity: "60%",
    fontSize: "16px",
    maxWidth: "80%",
  },
};

export default function SurveyResults() {
  const { setErrorAlert } = useContext(AlertContext);
  const {
    fairPaySurveyResponse,
    fairPaySurveyPartnerships,
    setFairPaySurveyPartnerships,
  } = useContext(FairPaySurveyResponseContext);
  const [loadingPartnerships, setLoadingPartnerships] =
    useState<boolean>(false);

  const getPartnershipResults = async () => {
    if (!fairPaySurveyResponse?.integrationEmail) {
      setFairPaySurveyPartnerships([]);
      return;
    }

    if (fairPaySurveyPartnerships) {
      return;
    }

    setLoadingPartnerships(true);

    try {
      const { surveyPartnerships } = await fetcher(
        `/api/organization/fair-pay-survey-responses/${fairPaySurveyResponse.id}/partnerships`,
        "GET",
      );
      setFairPaySurveyPartnerships(surveyPartnerships);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoadingPartnerships(false);
    }
  };

  useEffect(() => {
    getPartnershipResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} sx={styles.innerContainer} container>
        <Grid item xs={12} justifyContent="center" container>
          <Link
            href="https://onbento.com?s=fairpay"
            target="_blank"
            onClick={() =>
              trackEvent("Fair Pay - Bento Logo Clicked", {
                "Survey UUID": fairPaySurveyResponse?.surveyUuid,
              })
            }
          >
            <Box component="img" src={logo} sx={styles.logo} />
          </Link>
        </Grid>
        <Grid item xs={12} justifyContent="center" container>
          <Typography variant="h2" sx={styles.heading} paragraph>
            {fairPaySurveyResponse?.firstName
              ? `${capitalize(
                  fairPaySurveyResponse.firstName,
                )}, Here's your Fair Pay Report`
              : `Here's your Fair Pay Report`}
          </Typography>
          <Typography sx={styles.paragraph} gutterBottom>
            {fairPaySurveyResponse?.integrationEmail
              ? `We scanned your deals and compared them with thousands of other
          creators.`
              : `We compared your stats with thousands of other creators.`}
          </Typography>
          <Typography variant="caption" color="textSecondary" paragraph>
            <em>Brought to you by Bento. The creator pitching tool</em>
          </Typography>
        </Grid>
        <FairPayBadge surveyResponse={fairPaySurveyResponse} />
        {fairPaySurveyResponse?.followerCount ? (
          <FollowersPercentile surveyResponse={fairPaySurveyResponse} />
        ) : (
          <></>
        )}
        <NichePercentile surveyResponse={fairPaySurveyResponse} />
        <LocationPercentile surveyResponse={fairPaySurveyResponse} />
        <CityPercentile surveyResponse={fairPaySurveyResponse} />
        {fairPaySurveyResponse?.highestEarningBrandIncome &&
        fairPaySurveyResponse?.highestEarningBrandIncome > 0 ? (
          <BiggestBrandDeal surveyResponse={fairPaySurveyResponse} />
        ) : (
          <></>
        )}
        <BrandBreakdown
          loadingPartnerships={loadingPartnerships}
          surveyResponse={fairPaySurveyResponse}
          surveyPartnerships={fairPaySurveyPartnerships}
        />

        <FairPayShareBadge surveyResponse={fairPaySurveyResponse} />
      </Grid>
    </Grid>
  );
}
